import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  InjectionToken,
  LOCALE_ID,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';

import '@angular/common/locales/global/de';
import '@angular/common/locales/global/en';
import {
  AUTH_LOGIN_REDIRECT,
  AUTH_STORAGE,
  provideSanctumAuthentification,
  provideXsrfConfiguration,
  sanctumInterceptor,
  xsrfInterceptor,
} from '@freesixtyfive/auth';
import { provideUserManagement } from '@freesixtyfive/fe-user-management';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { appRoutes } from './app.routes';
import { CustomPreset } from './custom-preset';
import { ManagedUser } from './shared/models/user-management.model';

import { provideEchartsCore } from 'ngx-echarts';
// import echarts core
import { echarts } from './echarts.config';

export const EnvironmentToken = new InjectionToken<typeof environment>(
  'app.environment',
);

export const GoogleMapsToken = new InjectionToken<
  (typeof environment)['googleMaps']
>('app.environment.googleMaps');

export const appConfig: ApplicationConfig = {
  providers: [
    // provideExperimentalZonelessChangeDetection(),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: CustomPreset,
        options: {
          // prefix: 'p',
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    provideHttpClient(withInterceptors([xsrfInterceptor, sanctumInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'de',
    }),
    provideXsrfConfiguration({
      requestUrl: `${process.env.FE_API_BASE}/sanctum/csrf-cookie`,
      sendAlways: true,
    }),
    provideSanctumAuthentification({
      signInUrl: `${process.env.FE_API_BASE}/auth/login`,
      signOutUrl: `${process.env.FE_API_BASE}/auth/logout`,
      forgotPwUrl: `${process.env.FE_API_BASE}/api/auth/users/forgotPassword`,
      resetPwUrl: `${process.env.FE_API_BASE}/api/auth/users/resetPassword`,
    }),

    // importProvidersFrom([BrowserAnimationsModule]),
    { provide: AUTH_LOGIN_REDIRECT, useValue: '/login' },
    { provide: AUTH_STORAGE, useFactory: () => inject(WA_LOCAL_STORAGE) },
    // { provide: API_BASE, useValue: process.env.FE_API_BASE },
    // pipe formats only (date/number); UI language via ngx-translate
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: EnvironmentToken, useValue: environment },
    {
      provide: GoogleMapsToken,
      useValue: environment.googleMaps,
    },
    provideEchartsCore({ echarts }),
    provideUserManagement({
      apiEndpoint: `${process.env.FE_API_BASE}/api/manage/users`,
      userSchema: ManagedUser,
    }),
  ],
};
